import { cliente } from '@/lib/cliente';
import type {
  ActualizarSolicitudContadoSchema,
  CrearSolicitudContadoSchema,
} from '@adelante/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { z } from 'zod';

export function useUsuarioActual() {
  return useQuery({
    queryKey: ['yo'],
    queryFn: () => cliente.yo.obtener(),
    staleTime: 10 * 60 * 1000,
  });
}

export function useSolicitudesContado() {
  return useQuery({
    queryKey: ['solicitudes-contado'],
    queryFn: () => cliente.solicitudesContado.listar(),
    refetchOnWindowFocus: true,
  });
}

/** Órdenes de compra del proveedor, leídas en vivo de BC. Solo se pide cuando hay proveedor elegido. */
export function useOrdenesCompraBC(vendorCode: string | null) {
  return useQuery({
    queryKey: ['bc-ordenes-compra', vendorCode],
    queryFn: () => cliente.ordenesCompraBC.porProveedor(vendorCode as string),
    enabled: !!vendorCode,
    staleTime: 2 * 60 * 1000,
  });
}

/** Cuántas OC vigentes tiene cada proveedor (vendorCode → cantidad), para ordenar la lista de proveedores. */
export function useOrdenesPorProveedor(habilitado = true) {
  return useQuery({
    queryKey: ['bc-ordenes-resumen'],
    queryFn: async () => {
      const r = await cliente.ordenesCompraBC.resumen();
      return Object.fromEntries((r.data ?? []).map((x) => [x.vendorCode, x.cantidad]));
    },
    enabled: habilitado,
    staleTime: 2 * 60 * 1000,
  });
}

/** Encabezado y líneas de una orden (con su obra), de BC. Solo se pide cuando hay una orden elegida. */
export function useOrdenCompraDetalle(numero: string | null) {
  return useQuery({
    queryKey: ['bc-orden-compra-detalle', numero],
    queryFn: () => cliente.ordenesCompraBC.detalle(numero as string),
    enabled: !!numero,
    staleTime: 2 * 60 * 1000,
  });
}

export function useAccionesSolicitudContado() {
  const queryClient = useQueryClient();
  // Aprobar, reabrir o borrar una solicitud también cambia las listas de pagos (y su aviso de lista abierta).
  const refrescar = () => {
    queryClient.invalidateQueries({ queryKey: ['solicitudes-contado'] });
    queryClient.invalidateQueries({ queryKey: ['listas-contado'] });
  };
  return {
    crear: useMutation({
      mutationFn: (d: z.infer<typeof CrearSolicitudContadoSchema>) =>
        cliente.solicitudesContado.crear(d),
      onSuccess: refrescar,
    }),
    actualizar: useMutation({
      mutationFn: ({
        id,
        d,
      }: { id: number; d: z.infer<typeof ActualizarSolicitudContadoSchema> }) =>
        cliente.solicitudesContado.actualizar(id, d),
      onSuccess: refrescar,
    }),
    eliminar: useMutation({
      mutationFn: (id: number) => cliente.solicitudesContado.eliminar(id),
      onSuccess: refrescar,
    }),
    aprobar: useMutation({
      mutationFn: ({
        id,
        monto,
        documentos,
      }: { id: number; monto?: number; documentos?: string[] }) =>
        cliente.solicitudesContado.aprobar(id, monto, documentos),
      onSuccess: refrescar,
    }),
    rechazar: useMutation({
      mutationFn: ({ id, motivo }: { id: number; motivo: string | null }) =>
        cliente.solicitudesContado.rechazar(id, motivo),
      onSuccess: refrescar,
    }),
    reabrir: useMutation({
      mutationFn: (id: number) => cliente.solicitudesContado.reabrir(id),
      onSuccess: refrescar,
    }),
    liberarSaldo: useMutation({
      mutationFn: (id: number) => cliente.solicitudesContado.liberarSaldo(id),
      onSuccess: refrescar,
    }),
  };
}
